
import { useState, useEffect } from 'react';
import { Button, FormControl, Link, Typography } from '@mui/material';
import { AccountMenu, AppMenu } from "@colliers/colliers-b2c";
import MenuItem from "@mui/material/MenuItem";
import { axiosGet, axiosPost } from '../utils/Axios';
import { GetModuleDetailsUrl, logoutUrl } from '../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LanguageSelector } from './languageSelector';

const Header = ({ authChecked }) => {
    const [moduleData, setModuleData] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    const userName = userInfo?.userName;
    const userRole = userInfo?.roleName;

    useEffect(() => {
        const currentPath = location.pathname.toLowerCase();
        const publicPaths = ['/cookie-policy', '/privacy-policy', '/terms-of-use', '/access-denied'];

        if (!userInfo) {
            setIsLoggedIn(false);
            if (authChecked && !publicPaths.includes(currentPath)) {
                window.location.href = "/Login/KeyStone_Login";
            }
            return;
        }

        const getModuleData = async () => {
            try {
                const response = await axiosGet(GetModuleDetailsUrl);

                if (Array.isArray(response)) {
                    setIsLoggedIn(true);
                    setModuleData(response);

                    if (publicPaths.includes(currentPath)) {
                        return;
                    }

                    let bestMatch = null;
                    let bestMatchLength = 0;

                    response.forEach(module => {
                        const moduleName = module.name.toLowerCase().replace(/\s+/g, '-');
                        const path = currentPath;

                        if (
                            path === '/' + moduleName ||
                            ((moduleName === 'audit-log' || moduleName === 'audit-trail') &&
                                (path === '/audit-log' || path === '/audit-trail'))
                        ) {
                            bestMatch = module;
                            bestMatchLength = moduleName.length;
                            return;
                        }

                        if (moduleName === "dashboard") {
                            const isDashboardAllowed =
                                path === "/dashboard" ||
                                path.startsWith("/dashboard/") ||
                                /^\/dashboard(?!-)/.test(path) ||
                                path.startsWith("/dashboardadd") ||
                                path.startsWith("/loaddashboard");

                            if (isDashboardAllowed && moduleName.length > bestMatchLength) {
                                bestMatch = module;
                                bestMatchLength = moduleName.length;
                            }
                            return;
                        }

                        if (path.startsWith("/" + moduleName) && moduleName.length > bestMatchLength) {
                            bestMatch = module;
                            bestMatchLength = moduleName.length;
                        }
                    });

                    if (!bestMatch && currentPath === '/') {
                        bestMatch = response.find(mod => mod.name.toLowerCase() === 'dashboard');
                    }

                    if (!bestMatch) {
                        navigate('/access-denied');
                        return;
                    }

                    localStorage.setItem('matchedModule', JSON.stringify(bestMatch));

                    if (bestMatch.read !== true) {
                        navigate('/access-denied');
                        return;
                    }

                } else {
                    localStorage.removeItem("userInfo");
                    setIsLoggedIn(false);
                    setModuleData([]);
                    console.error("Failed to fetch modules: No data found.");
                }

            } catch (error) {
                localStorage.removeItem("userInfo");
                setIsLoggedIn(false);
                console.error("Error fetching modules:", error?.response || error);
            }
        };

        getModuleData();

    }, [navigate, location.pathname, userName, authChecked]);

    const handleNavigation = (formattedName) => {
        navigate(`/${formattedName}`);
    };
    const accountMenuLinks = () => {
        return (
            <>
                <FormControl className=' border-size-none' fullWidth size="small" sx={{ mt: 2 }}>
                    <LanguageSelector className="padding-left-base" />
                </FormControl>
                {/* <Divider /> */}
                {moduleData.length > 0
                    ? moduleData.filter(module => module.name !== 'Dashboard').map((module) => {
                        const formattedName = module.name.toLowerCase().replace(/\s+/g, '-');
                        return (
                            <MenuItem className='padding-bottom-s margin-bottom-base header' key={module.name} onClick={() => handleNavigation(formattedName)}>
                                {module.name}
                            </MenuItem>
                        );
                    })
                    : null}
            </>
        );
    };

    const getReturnPath = (path) => {
        switch (path) {
            case "/colliers-user-management-manage":
                return "/colliers-user-management";
            case "/client-user-management-manage":
                return "/client-user-management";
            case "/dashboard-group-add":
                return "/dashboard-group";
            case "/country-manage":
                return "/country";
            default:
                return "/dashboard";
        }
    };

    const getReturnText = (path) => {
        switch (path) {
            case "/colliers-user-management-manage":
                return "Return to Colliers User Management";
            case "/client-user-management-manage":
                return "Return to Client User Management";
            case "/dashboard-group-add":
                return "Return to Dashboard Group";
            case "/country-manage":
                return "Return to Country";
            default:
                return "Return to My Dashboard";
        }
    };

    const mobileAccountInfo = isLoggedIn
        ? {
            name: userName,
            email: userRole,
            links: accountMenuLinks(),
            profileImageUrl: 'img/user-profile.png'
        }
        : {
            name: "",
            email: "",
            links: null,
            profileImageUrl: ""
        };

    const isOnDashboard = location.pathname;
    const handleBackClick = () => {
        const returnPath = getReturnPath(location.pathname);
        navigate(returnPath);
    };
    return (
        <AppMenu
            menuLinks={
                !isLoggedIn || isOnDashboard === "/dashboard" || isOnDashboard === "/" ? (
                    <></>
                ) : (
                    <Link className='cack-to-dash menuLink'
                        onClick={handleBackClick}
                    >
                        <ArrowBackIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                            {getReturnText(isOnDashboard)}
                        </Typography>
                    </Link>
                )
            }
            primaryLogo={
                <Link href="/">
                    <img src="img/header-logo.png" alt="Colliers" />
                </Link>
            }
            accountMenu={
                isLoggedIn ? (
                    <AccountMenu
                        name={mobileAccountInfo.name}
                        email={mobileAccountInfo.email}
                        links={mobileAccountInfo.links}
                        profileImageUrl={mobileAccountInfo.profileImageUrl}
                        bottomActionOnClick={{
                            onclick: async () => {
                                console.log("Logout action");
                                localStorage.clear();
                                sessionStorage.clear();
                                try {
                                    const response = await axiosPost(logoutUrl);
                                    if (response) {
                                        window.location.href = "/Login/KeyStone_Login";
                                    }
                                } catch (error) {
                                    console.error("Error during logout:", error);
                                }
                            }
                        }}
                    />
                ) : (
                    <Button href="/Login/KeyStone_Login" variant="text">Sign In</Button>
                )
            }
            mobileAccountInfo={mobileAccountInfo}
        />
    );
};

export default Header;
